import React, { useEffect, useState } from 'react';
import API from '../../Api';

const ROLES = [
  { id: 'USER', label: 'User' },
  { id: 'ADMIN', label: 'Admin' },
  { id: 'ENGINEER', label: 'Engineer' },
  { id: 'VIEWER', label: 'Viewer' },
];

export default function SettingsOrganization() {
  const [org, setOrg] = useState(null);
  const [members, setMembers] = useState([]);
  const [pendingInvitations, setPendingInvitations] = useState([]);
  const [name, setName] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(true);
  const [inviteOpen, setInviteOpen] = useState(false);
  const [inviteForm, setInviteForm] = useState({ username: '', email: '', title: '', orgRole: 'USER' });
  const [inviteResult, setInviteResult] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    setError('');
    try {
      // Sequential on purpose: both endpoints provision/backfill the workspace.
      // Parallel calls contended on users/workspaces and hung login.
      const orgRes = await API.get('/api/organization', { timeout: 15000 });
      setOrg(orgRes.data);
      setName(orgRes.data?.name || '');
      const membersRes = await API.get('/api/organization/members', { timeout: 15000 });
      setMembers(membersRes.data?.members || []);
      setPendingInvitations(membersRes.data?.pendingInvitations || []);
    } catch (err) {
      setError(err.response?.data?.error || err.response?.data?.message || 'Failed to load organization');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const saveOrg = async () => {
    setSaving(true);
    setError('');
    setSuccess('');
    try {
      const res = await API.put('/api/organization', { name });
      setOrg(res.data);
      setSuccess('Organization updated');
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to update organization');
    } finally {
      setSaving(false);
    }
  };

  const invite = async () => {
    if (!inviteForm.username.trim()) {
      setError('Username is required');
      return;
    }
    if (!inviteForm.email.trim() || !inviteForm.email.includes('@')) {
      setError('A valid email is required so the user can be notified');
      return;
    }
    setSaving(true);
    setError('');
    try {
      const res = await API.post('/api/organization/members', inviteForm);
      setInviteResult(res.data);
      setInviteOpen(false);
      setInviteForm({ username: '', email: '', title: '', orgRole: 'USER' });
      await load();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to invite user');
    } finally {
      setSaving(false);
    }
  };

  const changeRole = async (memberId, orgRole) => {
    setError('');
    try {
      await API.put(`/api/organization/members/${memberId}/role`, { orgRole });
      await load();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to change role');
    }
  };

  const removeMember = async (memberId) => {
    if (!window.confirm('Remove this user from the organization?')) return;
    setError('');
    try {
      await API.delete(`/api/organization/members/${memberId}`);
      await load();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to remove user');
    }
  };

  if (loading) {
    return <div className="settings-panel">Loading organization…</div>;
  }

  return (
    <div className="settings-panel">
      <h2>Organization</h2>
      <p className="settings-subtitle">Company details, seats, and team members.</p>
      {error && <div className="settings-alert settings-alert--error">{error}</div>}
      {success && <div className="settings-alert settings-alert--success">{success}</div>}

      <div className="settings-field">
        <label htmlFor="org-name">Company Name</label>
        <input
          id="org-name"
          value={name}
          onChange={(e) => setName(e.target.value)}
          disabled={!org?.canManageMembers}
        />
      </div>
      <div className="settings-stat-row">
        <div>
          <strong>Plan</strong>
          <div>{org?.planLabel || org?.plan || '—'}</div>
        </div>
        <div>
          <strong>Users</strong>
          <div>
            {org?.seatsUsed ?? '—'} / {org?.maxSeats ?? '—'} seats used
          </div>
        </div>
      </div>
      {org?.canManageMembers && (
        <div className="settings-actions">
          <button type="button" className="settings-btn settings-btn--primary" onClick={saveOrg} disabled={saving}>
            Save Organization
          </button>
        </div>
      )}

      <div className="settings-divider" />
      <div className="settings-actions" style={{ justifyContent: 'space-between', alignItems: 'center' }}>
        <h2 style={{ fontSize: 20, margin: 0 }}>Users</h2>
        {org?.canManageMembers && (
          <button type="button" className="settings-btn settings-btn--secondary" onClick={() => setInviteOpen(true)}>
            + Invite User
          </button>
        )}
      </div>

      {members.map((member) => (
        <div key={member.id} className="settings-member-row">
          <div>
            <strong>{member.displayName || member.username}</strong>
            <div style={{ color: '#6b7280', fontSize: 13 }}>
              {member.username}
              {member.email ? ` · ${member.email}` : ''}
            </div>
          </div>
          <div className="settings-actions">
            {member.isOwner || !org?.canManageMembers ? (
              <span className="settings-readonly" style={{ padding: '8px 12px' }}>
                {member.orgRoleLabel}
              </span>
            ) : (
              <select
                value={member.orgRole}
                onChange={(e) => changeRole(member.id, e.target.value)}
                style={{ minWidth: 120 }}
              >
                {ROLES.map((role) => (
                  <option key={role.id} value={role.id}>
                    {role.label}
                  </option>
                ))}
              </select>
            )}
            {org?.canManageMembers && !member.isOwner && (
              <button type="button" className="settings-btn settings-btn--danger" onClick={() => removeMember(member.id)}>
                Remove
              </button>
            )}
          </div>
        </div>
      ))}

      {pendingInvitations.length > 0 && (
        <>
          <h2 style={{ fontSize: 20, margin: '24px 0 12px' }}>Pending invitations</h2>
          {pendingInvitations.map((invite) => (
            <div key={invite.id} className="settings-member-row">
              <div>
                <strong>{invite.username}</strong>
                <div style={{ color: '#6b7280', fontSize: 13 }}>
                  {invite.email} · {invite.orgRole} · waiting to accept
                </div>
              </div>
            </div>
          ))}
        </>
      )}

      {inviteOpen && (
        <div className="settings-modal-backdrop" onClick={() => setInviteOpen(false)}>
          <div className="settings-modal" onClick={(e) => e.stopPropagation()}>
            <h2 style={{ marginTop: 0, fontSize: 22 }}>Invite User</h2>
            <div className="settings-field">
              <label>Username</label>
              <input
                value={inviteForm.username}
                onChange={(e) => setInviteForm((f) => ({ ...f, username: e.target.value }))}
              />
            </div>
            <div className="settings-field">
              <label>Name</label>
              <input
                value={inviteForm.title}
                onChange={(e) => setInviteForm((f) => ({ ...f, title: e.target.value }))}
              />
            </div>
            <div className="settings-field">
              <label>Email</label>
              <input
                type="email"
                required
                placeholder="user@company.com"
                value={inviteForm.email}
                onChange={(e) => setInviteForm((f) => ({ ...f, email: e.target.value }))}
              />
            </div>
            <div className="settings-field">
              <label>Role</label>
              <select
                value={inviteForm.orgRole}
                onChange={(e) => setInviteForm((f) => ({ ...f, orgRole: e.target.value }))}
              >
                {ROLES.map((role) => (
                  <option key={role.id} value={role.id}>
                    {role.label}
                  </option>
                ))}
              </select>
            </div>
            <div className="settings-actions">
              <button type="button" className="settings-btn settings-btn--primary" onClick={invite} disabled={saving}>
                Invite
              </button>
              <button type="button" className="settings-btn settings-btn--secondary" onClick={() => setInviteOpen(false)}>
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {inviteResult && (
        <div className="settings-modal-backdrop" onClick={() => setInviteResult(null)}>
          <div className="settings-modal" onClick={(e) => e.stopPropagation()}>
            <h2 style={{ marginTop: 0, fontSize: 22 }}>Invitation sent</h2>
            {inviteResult.emailSent ? (
              <p>
                An invitation was emailed to <strong>{inviteResult.email}</strong>.
                They will join this organization after accepting the link — they will not create a new workspace.
              </p>
            ) : (
              <p style={{ color: '#b45309' }}>
                {inviteResult.emailWarning || inviteResult.message || 'Invitation saved, but the email was not sent.'}
              </p>
            )}
            <div className="settings-actions">
              <button type="button" className="settings-btn settings-btn--secondary" onClick={() => setInviteResult(null)}>
                Done
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
